import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { DownloadOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import {
  fetchClaimFormDefinitions,
  downloadClaimFormDefinitionDocx,
} from "@accredo/fe-shared/services/claimFormService.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";

export default function ClaimFormHistoryTab({ claimFormId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [definitions, setDefinitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownload = async (definition) => {
    setDownloadingId(definition.id);
    try {
      await downloadClaimFormDefinitionDocx(
        claimFormId,
        definition.id,
        `${definition.docx_template}.docx`
      );
    } catch (error) {
      enqueueSnackbar("DOCX-Vorlage konnte nicht heruntergeladen werden", {
        variant: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const load = useCallback(() => {
    setIsLoading(true);
    fetchClaimFormDefinitions(claimFormId)
      .then((res) => setDefinitions(res?.data ?? []))
      .catch(() =>
        enqueueSnackbar("Verlauf konnte nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setIsLoading(false));
  }, [claimFormId, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Paper variant="outlined">
      {definitions.length > 0 ? (
        <List disablePadding>
          {definitions.map((definition, i) => (
            <Box key={definition.id}>
              {i > 0 && <Divider />}
              <ListItem
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {definition.is_current && (
                      <Chip size="small" label="aktuell" color="success" />
                    )}
                    {definition.docx_template && (
                      <Tooltip title="DOCX-Vorlage herunterladen">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(definition)}
                          disabled={downloadingId === definition.id}
                        >
                          <DownloadOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <ListItemText
                  primary={definition.definition_file_name ?? `Definition #${definition.id}`}
                  secondary={
                    <>
                      DOCX-Vorlage: {definition.docx_template ?? "–"}
                      {" · "}
                      {getDatum(definition.created_at)}
                    </>
                  }
                />
              </ListItem>
            </Box>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Keine Definitionen vorhanden
        </Typography>
      )}
    </Paper>
  );
}
